import { stateValueIssue } from '../data/state-value-rules-2025.ts';
import {
  ATTACHMENT_FIELDS,
  COMPANY_BINDINGS,
  OWNER_BINDINGS,
  EMAIL_BINDINGS,
  RMP_BINDINGS,
  ANNUAL_FILING_BINDINGS,
  STATEWIDE_LEPC_BINDINGS,
  stateFieldContext,
  stateFieldApplies,
  present,
} from '../data/state-rules-2025.ts';
import catalog from '../data/state-fields-2025.json' with { type: 'json' };
import { isSupportedStateField } from './state-field-identifiers.ts';
import type {
  StateFieldValue,
  TierIIStateReporting,
  TierIIDocumentKind,
} from '../../tri-shared/types/tierii-state-fields.ts';
import type { FacilityMeta } from './tier2submit-export.ts';
import type { TierIIReportFormEntry } from '../types/reporting.ts';
import type { FieldApplicability } from './field-applicability.ts';
import { chemicalCarrierNames, isChemicalCarrierField } from './chemical-carriers.ts';

export interface StateFieldDefinition {
  field: string;
  label: string;
  required: boolean;
  choices?: string[];
  choiceValues?: number[];
  searchLabel: string | null;
}
export interface StateDocumentStatus {
  fileCount: number;
  kinds: TierIIDocumentKind[];
  sdsComplete: boolean;
  missingSds: string[];
}
export interface ResolvedStateField extends StateFieldDefinition {
  requested: boolean;
  value: StateFieldValue;
  source: string | null;
}
export const STATE_FIELDS_YEAR = catalog.reportingYear;
/** New facilityTierIIStatuses rows default to this shape in the database (see the tierii-state-reporting migration). */
export const emptyStateReporting = (): TierIIStateReporting => ({
  supplemental: {},
  chemicalCarriers: {},
  stateCorrectionReview: null,
  facilityOverrides: {},
  applicability: { newOrUpdatedFacility: null, sitePlanRequested: null },
  productSdsApplicability: {},
  includeProductSds: false,
  productSds: {},
  facility: {},
  chemicals: {},
  documents: [],
});
/** Document coverage before any report documents have been resolved: nothing is attached. */
export const emptyStateDocumentStatus = (): StateDocumentStatus => ({
  fileCount: 0,
  kinds: [],
  sdsComplete: false,
  missingSds: [],
});
export const reportEntryKey = (entry: TierIIReportFormEntry) =>
  entry.basis === 'compound' ? `compound-${entry.casOrCategory}` : `mixture-${entry.productID}`;
export function stateFieldDefinitions(
  scope: 'facility' | 'chemical',
  state: string,
  year: number,
): StateFieldDefinition[] {
  if (year !== STATE_FIELDS_YEAR) return [];
  return (
    ((scope === 'facility' ? catalog.facilities : catalog.chemicals) as Record<string, StateFieldDefinition[]>)[
      state
    ] ?? []
  );
}
export function resolveFacilityStateFields(meta: FacilityMeta): ResolvedStateField[] {
  const state = meta.state;
  const documents = meta.documentStatus;
  const saved = meta.stateReporting.facility[state] ?? {};
  return stateFieldDefinitions('facility', state, meta.reportingYear).map((def) => {
    let value: StateFieldValue = saved[def.field] ?? null;
    let source: string | null = null;
    const key = `${state}:${def.field}`;
    if (STATEWIDE_LEPC_BINDINGS.includes(key) && def.choices?.length === 1) {
      value = def.choices[0]!;
      source = 'Statewide Local Emergency Planning Committee';
    }
    if (ANNUAL_FILING_BINDINGS.includes(key)) {
      value = saved.bool01 !== true && saved.bool02 !== true;
      source = 'Annual report; Initial and Updated filing selections take precedence';
    }
    if (COMPANY_BINDINGS.includes(key)) {
      const company = meta.name;
      if (company) {
        value = company;
        source = 'Facility name';
      }
    }
    if (OWNER_BINDINGS.includes(key)) {
      const owner = meta.contacts?.find((c) => c.contactTypes.includes('Owner / Operator'))?.name;
      if (owner) {
        value = owner;
        source = 'Owner / operator contact';
      }
    }
    if (EMAIL_BINDINGS.includes(key)) {
      const email = meta.contacts?.find((c) => c.contactTypes.includes('Tier II Information Contact'))?.email;
      if (email) {
        value = email;
        source = 'Tier II information contact';
      }
    }
    if (RMP_BINDINGS.includes(key) && meta.subjectToSection112r != null) {
      value = key === 'NC:number02' ? (meta.subjectToSection112r ? 1 : 0) : meta.subjectToSection112r ? 'Yes' : 'No';
      source = 'CAA Section 112(r) answer';
    }
    const kind = ATTACHMENT_FIELDS[state]?.[def.field];
    if (kind) {
      const attached = kind === 'sds' ? documents.sdsComplete : documents.kinds.includes(kind);
      value = def.field.startsWith('text') ? (attached ? 'Yes' : 'No') : attached;
      source = 'Report attachments';
    }
    return { ...def, value, source, requested: false };
  });
}
export function chemicalStateFieldDefinitions(
  state: string,
  year: number,
  entry: Pick<TierIIReportFormEntry, 'isEHS'>,
): StateFieldDefinition[] {
  return stateFieldDefinitions('chemical', state, year).map((field) => ({
    ...field,
    // Massachusetts and Vermont only require these shipment facts for EHS entries.
    // https://www.mass.gov/doc/massachusetts-hazconnect-user-guide-pdf-2025/download
    required: ['MA', 'VT'].includes(state) && entry.isEHS === false ? false : field.required,
  }));
}
export function resolveChemicalStateFields(meta: FacilityMeta, entry: TierIIReportFormEntry): ResolvedStateField[] {
  const saved = meta.stateReporting.chemicals[meta.state]?.[reportEntryKey(entry)] ?? {};
  return chemicalStateFieldDefinitions(meta.state, meta.reportingYear, entry).map((def) => {
    const value = isChemicalCarrierField(def)
      ? chemicalCarrierNames(meta.stateReporting, meta.state, reportEntryKey(entry))
      : (saved[def.field] ?? null);
    return {
      ...def,
      value,
      source: null,
      requested: false,
    };
  });
}
export function stateFieldsForForm(fields: ResolvedStateField[], state: string, scope: 'facility' | 'chemical') {
  const { required, requested } = stateFieldContext(fields, state, scope);
  return fields.map((field) => ({
    ...field,
    required: required.has(field.field),
    requested: requested.has(field.field),
    applicability: (required.has(field.field)
      ? 'required'
      : requested.has(field.field)
        ? 'requested'
        : stateFieldApplies(fields, state, scope, field.field)
          ? 'optional'
          : 'not-applicable') as FieldApplicability,
  }));
}
export function stateFieldIssues(
  fields: ResolvedStateField[],
  state: string,
  scope: 'facility' | 'chemical',
): string[] {
  const { required, issues } = stateFieldContext(fields, state, scope);
  for (const field of fields) {
    if (!isSupportedStateField(field.field)) {
      issues.push(
        `Unsupported state field ${field.field} (${field.label}); update the state catalog integration before exporting.`,
      );
      continue;
    }
    const v = field.value;
    if (required.has(field.field) && !present(v)) {
      issues.push(`${field.label} is required.`);
      continue;
    }
    if (!present(v)) continue;
    if (scope === 'facility') {
      const issue = stateValueIssue(state, field.field, v);
      if (issue) issues.push(issue);
    }
    const choices = field.choiceValues ?? field.choices;
    if (choices && !(choices as (string | number)[]).includes(v as string | number))
      issues.push(`Choose a listed value for ${field.label}.`);
    if (field.field.startsWith('number') && (!Number.isFinite(v) || typeof v !== 'number' || v < 0))
      issues.push(`${field.label} must be a nonnegative number.`);
    if (
      field.field.startsWith('date') &&
      (typeof v !== 'string' ||
        !/^\d{4}-\d{2}-\d{2}$/.test(v) ||
        !Number.isFinite(Date.parse(v)) ||
        new Date(v).toISOString().slice(0, 10) !== v)
    )
      issues.push(`${field.label} must be a valid date.`);
    if (field.field.startsWith('bool') && typeof v !== 'boolean')
      issues.push(`${field.label} must be answered Yes or No.`);
    if (
      scope === 'facility' &&
      ATTACHMENT_FIELDS[state]?.[field.field] &&
      required.has(field.field) &&
      v !== true &&
      v !== 'Yes'
    )
      issues.push(`A report attachment is needed: ${field.label.replace(/[.]$/, '')}.`);
  }
  return issues;
}
